import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { WalletIcon, PlusIcon, TrashIcon, SpinnerGapIcon, PencilSimpleIcon, CheckIcon } from "@phosphor-icons/react";
import { fetchWatchlist } from "../lib/api";
import { useHoldings } from "../context/HoldingsContext";
import { isValidSolanaAddress, shortAddr } from "../lib/solana";
import { fmtUsd, fmtPrice, fmtPct, changeColor } from "../lib/format";
import { TradeButton } from "../components/TradeButton";

const Fallback = ({ symbol }) => (
  <div className="w-11 h-11 flex items-center justify-center bg-white/5 border border-white/10 font-mono text-xs text-muted-foreground shrink-0">
    {(symbol || "?").slice(0, 3)}
  </div>
);

function HoldingCard({ holding, coin, onUpdate, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(holding.amount ?? ""));
  const amount = Number(holding.amount) || 0;
  const price = coin?.priceUsd || 0;
  const value = price * amount;
  const ch24 = coin?.priceChange?.h24 ?? 0;

  const save = () => { onUpdate(holding.address, Number(val) || 0); setEditing(false); };

  return (
    <div data-testid={`holding-card-${holding.address}`} className="border border-white/10 bg-card p-4 space-y-3">
      <div className="flex items-start gap-3">
        {coin?.image ? (
          <img src={coin.image} alt={coin.symbol} className="w-11 h-11 object-cover border border-white/10 shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <Fallback symbol={coin?.symbol} />
        )}
        <div className="min-w-0 flex-1">
          <div className="font-heading font-bold text-base truncate">{coin?.symbol || shortAddr(holding.address)}</div>
          <div className="text-xs text-muted-foreground truncate">{coin?.name || (coin ? "" : "Not found on DexScreener")}</div>
        </div>
        <button data-testid={`remove-holding-${holding.address}`} onClick={() => onRemove(holding.address)} className="p-1.5 text-muted-foreground hover:text-danger hover:bg-white/5 transition-colors shrink-0" aria-label="remove">
          <TrashIcon size={16} />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Price</div>
          <div className="font-mono text-sm mt-0.5">{coin ? fmtPrice(price) : "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">24h</div>
          <div className="font-mono text-sm mt-0.5" style={{ color: changeColor(ch24) }}>{coin ? fmtPct(ch24) : "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">MCap</div>
          <div className="font-mono text-sm mt-0.5">{coin ? fmtUsd(coin.marketCap) : "—"}</div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-white/5 pt-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Holding</div>
          {editing ? (
            <div className="flex items-center gap-1 mt-1">
              <input data-testid={`amount-input-${holding.address}`} value={val} onChange={(e) => setVal(e.target.value)} type="number" className="w-24 bg-black/40 border border-white/10 px-2 py-1 text-sm font-mono focus:outline-none focus:border-neon/50" />
              <button data-testid={`amount-save-${holding.address}`} onClick={save} className="p-1.5 bg-neon text-black"><CheckIcon size={14} weight="bold" /></button>
            </div>
          ) : (
            <button onClick={() => { setVal(String(amount)); setEditing(true); }} className="flex items-center gap-1.5 mt-0.5 group">
              <span className="font-mono text-sm">{amount.toLocaleString()}</span>
              <PencilSimpleIcon size={13} className="text-muted-foreground group-hover:text-neon transition-colors" />
            </button>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Value</div>
          <div className="font-mono font-bold text-neon text-sm mt-0.5">{value > 0 ? fmtUsd(value) : "—"}</div>
        </div>
      </div>

      {coin && (
        <div className="pt-1">
          <TradeButton coin={coin} size="sm" />
        </div>
      )}
    </div>
  );
}

export default function MyWallet() {
  const { holdings, addHolding, updateAmount, removeHolding } = useHoldings();
  const [ca, setCa] = useState("");
  const [amount, setAmount] = useState("");

  const addresses = holdings.map((h) => h.address);
  const { data, isLoading } = useQuery({
    queryKey: ["holdings", addresses],
    queryFn: () => fetchWatchlist(addresses),
    enabled: addresses.length > 0,
    refetchInterval: 60000,
  });

  const coinsByAddr = {};
  (data?.coins || []).forEach((c) => { coinsByAddr[c.address] = c; });

  const totalValue = holdings.reduce((sum, h) => {
    const c = coinsByAddr[h.address];
    return sum + (c?.priceUsd || 0) * (Number(h.amount) || 0);
  }, 0);

  const onAdd = () => {
    const addr = ca.trim();
    if (!isValidSolanaAddress(addr)) { toast.error("Enter a valid Solana contract address."); return; }
    if (holdings.some((h) => h.address === addr)) { toast.error("That coin is already in your wallet."); return; }
    addHolding(addr, Number(amount) || 0);
    setCa(""); setAmount("");
    toast.success("Coin added to your wallet.");
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 flex items-center justify-center bg-neon text-black">
              <WalletIcon size={24} weight="bold" />
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">MY WALLET</h1>
          </div>
          <p className="text-muted-foreground mt-3 text-sm">Paste the coins you hold to track value & jump to buy more. Saved on this device.</p>
        </div>
        {holdings.length > 0 && (
          <div data-testid="portfolio-total" className="border border-neon/25 bg-neon/[0.03] px-5 py-3 text-right">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Est. portfolio value</div>
            <div className="font-mono font-black text-2xl text-neon">{fmtUsd(totalValue)}</div>
          </div>
        )}
      </div>

      {/* Add CA bar */}
      <div className="border border-white/10 bg-card p-4 flex flex-col sm:flex-row gap-3">
        <input
          data-testid="mywallet-ca-input"
          value={ca}
          onChange={(e) => setCa(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onAdd()}
          placeholder="Paste a coin contract address (CA)…"
          className="flex-1 bg-black/40 border border-white/10 px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-neon/50 transition-colors"
        />
        <input
          data-testid="mywallet-amount-input"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onAdd()}
          type="number"
          placeholder="Amount held (optional)"
          className="sm:w-52 bg-black/40 border border-white/10 px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-neon/50 transition-colors"
        />
        <button
          data-testid="mywallet-add-btn"
          onClick={onAdd}
          className="flex items-center justify-center gap-2 px-5 py-2.5 bg-neon text-black font-semibold text-sm hover:bg-neon/90 transition-colors"
        >
          <PlusIcon size={16} weight="bold" /> Add
        </button>
      </div>

      {holdings.length === 0 ? (
        <div className="border border-white/10 bg-card py-24 flex flex-col items-center gap-4 text-center">
          <WalletIcon size={40} className="text-muted-foreground" />
          <p className="text-muted-foreground font-mono text-sm">No coins added yet. Paste a CA above to start tracking your bags.</p>
          <Link to="/" data-testid="mywallet-go-radar" className="px-5 py-2.5 bg-neon text-black font-semibold text-sm hover:bg-neon/90 transition-colors">Browse the Radar</Link>
        </div>
      ) : isLoading ? (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
          <SpinnerGapIcon size={32} className="animate-spin text-neon" />
          <span className="font-mono text-sm">Loading your coins…</span>
        </div>
      ) : (
        <div data-testid="holdings-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {holdings.map((h) => (
            <HoldingCard key={h.address} holding={h} coin={coinsByAddr[h.address]} onUpdate={updateAmount} onRemove={removeHolding} />
          ))}
        </div>
      )}
    </div>
  );
}
